import { useCallback, useEffect, useState } from 'react';
import {
  CreditCard, Loader2, Play, Eye, AlertTriangle, RefreshCcw,
  CheckCircle2, Plus, History,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getStripeSyncStatus, previewStripeSync, executeStripeSync, listStripeSyncRuns,
  StripeSyncStatus, SyncResult, SyncRun, SyncAction,
} from '@/services/stripeSyncApi';

const getErrorMessage = (err: unknown): string => {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: string } } }).response;
    return response?.data?.error || '';
  }
  if (err && typeof err === 'object' && 'message' in err) {
    return (err as { message: string }).message;
  }
  return '';
};

const formatPrice = (cents: number) => `${(cents / 100).toFixed(2).replace('.', ',')} €`;

const ActionBadge = ({ action }: { action: SyncAction }) => {
  const styles: Record<SyncAction, string> = {
    created: 'bg-green-100 text-green-700',
    reused: 'bg-slate-100 text-slate-600',
    would_create: 'bg-blue-100 text-blue-700',
    would_reuse: 'bg-slate-100 text-slate-500',
  };
  const labels: Record<SyncAction, string> = {
    created: 'Créé',
    reused: 'Réutilisé',
    would_create: 'À créer',
    would_reuse: 'Existant',
  };
  const icons: Record<SyncAction, React.ReactNode> = {
    created: <Plus className="w-3 h-3" />,
    reused: <CheckCircle2 className="w-3 h-3" />,
    would_create: <Plus className="w-3 h-3" />,
    would_reuse: <CheckCircle2 className="w-3 h-3" />,
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${styles[action]}`}>
      {icons[action]}
      {labels[action]}
    </span>
  );
};

export default function StripeSyncPage() {
  const [status, setStatus] = useState<StripeSyncStatus | null>(null);
  const [preview, setPreview] = useState<SyncResult | null>(null);
  const [runs, setRuns] = useState<SyncRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [confirmExecute, setConfirmExecute] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([getStripeSyncStatus(), listStripeSyncRuns()]);
      setStatus(s);
      setRuns(r);
    } catch (err) {
      toast.error('Failed to load: ' + getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const result = await previewStripeSync();
      setPreview(result);
      const willCreate = result.plans.filter(
        (p) => p.productAction === 'would_create'
          || p.monthlyAction === 'would_create'
          || p.annualAction === 'would_create',
      ).length;
      toast.success(
        willCreate === 0
          ? 'Tout est déjà à jour côté Stripe.'
          : `Aperçu : ${willCreate} plan(s) avec des éléments à créer.`,
      );
    } catch (err) {
      toast.error('Aperçu échoué : ' + getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  const handleExecute = async () => {
    setExecuting(true);
    try {
      const result = await executeStripeSync();
      setPreview(null);
      toast.success('Synchronisation terminée.');
      // Refetch status pour montrer les IDs DB à jour
      const [s, r] = await Promise.all([getStripeSyncStatus(), listStripeSyncRuns()]);
      setStatus(s);
      setRuns(r);
      // On garde le résultat affiché en preview pour traçabilité immédiate
      setPreview(result);
    } catch (err) {
      toast.error('Synchronisation échouée : ' + getErrorMessage(err));
      try { setRuns(await listStripeSyncRuns()); } catch { /* ignore */ }
    } finally {
      setExecuting(false);
      setConfirmExecute(false);
    }
  };

  const isLive = status?.environment === 'live';

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <CreditCard className="w-6 h-6 text-slate-600" />
          <h1 className="text-2xl font-bold text-slate-800">Stripe Sync</h1>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Rafraîchir
        </button>
      </div>

      {/* Environment banner */}
      {status && (
        <div className={`mb-6 p-4 rounded-xl flex items-start gap-3 ${
          isLive ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200'
        }`}>
          <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isLive ? 'text-red-500' : 'text-blue-500'}`} />
          <div>
            <p className={`font-semibold text-sm ${isLive ? 'text-red-800' : 'text-blue-800'}`}>
              Environnement Stripe : <span className="uppercase">{status.environment}</span>
            </p>
            <p className={`text-sm ${isLive ? 'text-red-600' : 'text-blue-600'}`}>
              {isLive
                ? 'Toute action affecte le compte Stripe LIVE — facturations réelles.'
                : 'Compte Stripe TEST — sandbox sans impact financier.'}
            </p>
          </div>
        </div>
      )}

      {/* Plans table */}
      {loading && !status && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      )}

      {status && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm mb-6">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Plan</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tarification</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Prix mensuel</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Prix annuel</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Stripe IDs en DB</th>
                {preview && (
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Diff</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {status.codePlans.map((plan) => {
                const dbRow = status.dbPlans.find((p) => p.slug === plan.slug);
                const previewRow = preview?.plans.find((p) => p.slug === plan.slug);
                return (
                  <tr key={plan.slug} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="font-medium text-slate-800 text-sm">{plan.name}</div>
                      <code className="text-xs text-slate-500">{plan.slug}</code>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600">
                      {plan.pricingModel === 'flat' ? 'Forfait' : 'Par utilisateur'}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-700">
                      {formatPrice(plan.monthlyPriceCents)}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-700">
                      {formatPrice(plan.annualPriceCents)} <span className="text-xs text-slate-400">/mois</span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="text-xs space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400 w-16">Product</span>
                          <code className={dbRow?.stripe_product_id ? 'text-slate-700' : 'text-red-500'}>
                            {dbRow?.stripe_product_id || '— manquant —'}
                          </code>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400 w-16">Monthly</span>
                          <code className={dbRow?.stripe_price_monthly_id ? 'text-slate-700' : 'text-red-500'}>
                            {dbRow?.stripe_price_monthly_id || '— manquant —'}
                          </code>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400 w-16">Annual</span>
                          <code className={dbRow?.stripe_price_annual_id ? 'text-slate-700' : 'text-red-500'}>
                            {dbRow?.stripe_price_annual_id || '— manquant —'}
                          </code>
                        </div>
                      </div>
                    </td>
                    {preview && previewRow && (
                      <td className="px-5 py-4">
                        <div className="space-y-1">
                          <ActionBadge action={previewRow.productAction} />
                          <ActionBadge action={previewRow.monthlyAction} />
                          <ActionBadge action={previewRow.annualAction} />
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Actions */}
      {status && (
        <div className="flex items-center gap-3 mb-8">
          <button
            onClick={handlePreview}
            disabled={previewing || executing}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 rounded-lg transition-colors border border-blue-200"
          >
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            Aperçu (dry-run)
          </button>
          <button
            onClick={() => setConfirmExecute(true)}
            disabled={!preview || executing || previewing}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-slate-300 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {executing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Synchroniser
          </button>
          {!preview && (
            <span className="text-xs text-slate-500">Lancer un aperçu avant de synchroniser.</span>
          )}
        </div>
      )}

      {/* History */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <History className="w-4 h-4 text-slate-500" />
          <h2 className="text-sm font-semibold text-slate-700">Historique des synchronisations</h2>
        </div>
        {runs.length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-400 text-center">Aucune synchronisation enregistrée.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-5 py-2 text-xs font-semibold text-slate-500 uppercase">Date</th>
                <th className="text-left px-5 py-2 text-xs font-semibold text-slate-500 uppercase">Admin</th>
                <th className="text-left px-5 py-2 text-xs font-semibold text-slate-500 uppercase">Env</th>
                <th className="text-left px-5 py-2 text-xs font-semibold text-slate-500 uppercase">Statut</th>
                <th className="text-left px-5 py-2 text-xs font-semibold text-slate-500 uppercase">Durée</th>
                <th className="text-left px-5 py-2 text-xs font-semibold text-slate-500 uppercase">Détail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {runs.map((run) => (
                <tr key={run.id}>
                  <td className="px-5 py-2 text-sm text-slate-600">
                    {new Date(run.started_at).toLocaleString('fr-FR')}
                  </td>
                  <td className="px-5 py-2 text-sm text-slate-600">{run.triggered_by_email}</td>
                  <td className="px-5 py-2 text-sm">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase ${
                      run.environment === 'live' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {run.environment}
                    </span>
                  </td>
                  <td className="px-5 py-2 text-sm">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      run.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {run.status === 'success' ? 'Succès' : 'Erreur'}
                    </span>
                  </td>
                  <td className="px-5 py-2 text-xs text-slate-500">
                    {run.duration_ms != null ? `${run.duration_ms} ms` : '—'}
                  </td>
                  <td className="px-5 py-2 text-xs text-slate-600 max-w-md truncate">
                    {run.error_message || (run.summary
                      ? `${run.summary.plans.length} plan(s) sync.`
                      : '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirmation modal */}
      {confirmExecute && status && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Confirmer la synchronisation</h3>
            <p className="text-sm text-slate-600 mb-3">
              Cette action va créer ou réutiliser des Products et Prices dans le compte Stripe{' '}
              <strong className={isLive ? 'text-red-600' : 'text-blue-600'}>
                {status.environment.toUpperCase()}
              </strong>
              , puis mettre à jour <code className="text-xs bg-slate-100 px-1 rounded">plan_tiers</code> en base.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              L&apos;opération est idempotente : aucun doublon ne sera créé si tout est déjà aligné.
            </p>
            {isLive && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-xs font-semibold flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Compte Stripe LIVE — facturations réelles
                </p>
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmExecute(false)}
                disabled={executing}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleExecute}
                disabled={executing}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-300 rounded-lg transition-colors"
              >
                {executing && <Loader2 className="w-4 h-4 animate-spin" />}
                Synchroniser
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
